import React, { useState, useEffect } from 'react';
import './PlayerSetup.css';

const PlayerSetup = ({ gameMode, onStartGame, onBack }) => {
  const [humanPlayerColor, setHumanPlayerColor] = useState('white');
  const [aiDifficulty, setAiDifficulty] = useState('time-15'); // Default to 15 seconds
  const [aiSystem, setAiSystem] = useState('cpp_heuristic'); // 'cpp_heuristic' or 'cpp_nnue'
  const [opponentName, setOpponentName] = useState('');
  const [errors, setErrors] = useState({});
  const [humanTimeLimit, setHumanTimeLimit] = useState('1');
  const [aiTimeLimit, setAiTimeLimit] = useState('1');
  const [humanMoveTime, setHumanMoveTime] = useState('unlimited');
  const [aiMoveTime, setAiMoveTime] = useState('unlimited');
  const [importedGame, setImportedGame] = useState(null);
  const [importType, setImportType] = useState('csv');
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [useOpeningBook, setUseOpeningBook] = useState(true); // Default: enabled
  const [useSmartTime, setUseSmartTime] = useState(true); // Default: enabled

  const aiSystems = {
    'cpp_heuristic': {
      name: 'C++ Heuristic AI',
      description: 'Pure tree search with hand-crafted evaluation',
      icon: '⚡'
    },
    'cpp_nnue': {
      name: 'C++ NNUE AI',
      description: 'Neural network evaluation (trained)',
      icon: '🧠'
    }
  };

  const smartAiLevels = {
    'beginner': { name: 'Beginner', rating: 600, description: 'Perfect for learning' },
    'novice': { name: 'Novice', rating: 800, description: 'Basic strategy' },
    'amateur': { name: 'Amateur', rating: 1000, description: 'Casual play' },
    'intermediate': { name: 'Intermediate', rating: 1200, description: 'Solid moves' },
    'advanced': { name: 'Advanced', rating: 1400, description: 'Good tactics' },
    'expert': { name: 'Expert', rating: 1600, description: 'Strong play' },
    'master': { name: 'Master', rating: 1800, description: 'Excellent strategy' },
    'grandmaster': { name: 'Grandmaster', rating: 2000, description: 'Professional level' },
    'super_gm': { name: 'Super GM', rating: 2200, description: 'World-class play' }
  };

  const neuralAiLevels = {
    'latest': { name: '🔥 Latest Model', rating: '???', description: 'Auto-loads your most recent training' },
    'custom': { name: '🎯 Custom Model', rating: '???', description: 'Choose a specific trained model' },
    'development': { name: 'Development', rating: 1300, description: '50 self-play games trained' },
    'fast': { name: 'Fast', rating: 1500, description: '100 games, optimized for speed' },
    'reliable': { name: 'Reliable', rating: 1700, description: '200 games, balanced training' },
    'ultra-fast': { name: 'Ultra-Fast', rating: 1400, description: '20 games, lightning quick' },
    'micro-fast': { name: 'Micro-Fast', rating: 1200, description: '10 games, experimental' },
    'advanced-500': { name: 'Advanced (500)', rating: 1800, description: '500 games, strong AI' },
    'advanced-1000': { name: 'Advanced (1K)', rating: 2000, description: '1000 games, very strong AI' },
    'master-2000': { name: 'Master (2K)', rating: 2200, description: '2000 games, master-level AI' }
  };

  const cppAiLevels = {
    'time-1': { name: '1 second', rating: 1000, description: 'Instant response' },
    'time-2': { name: '2 seconds', rating: 1100, description: 'Very quick' },
    'time-3': { name: '3 seconds', rating: 1200, description: 'Rapid play' },
    'time-4': { name: '4 seconds', rating: 1300, description: 'Fast response' },
    'time-5': { name: '5 seconds', rating: 1400, description: 'Quick thinking' },
    'time-6': { name: '6 seconds', rating: 1450, description: 'Blitz speed' },
    'time-7': { name: '7 seconds', rating: 1500, description: 'Swift analysis' },
    'time-8': { name: '8 seconds', rating: 1550, description: 'Quick analysis' },
    'time-9': { name: '9 seconds', rating: 1580, description: 'Brisk play' },
    'time-10': { name: '10 seconds', rating: 1600, description: 'Fast play' },
    'time-15': { name: '15 seconds', rating: 1800, description: 'Standard thinking' },
    'time-30': { name: '30 seconds', rating: 2100, description: 'Strong analysis' },
    'time-45': { name: '45 seconds', rating: 2300, description: 'Deep thinking' },
    'time-60': { name: '1 minute', rating: 2500, description: 'Expert analysis' },
    'time-90': { name: '1.5 minutes', rating: 2700, description: 'Master level' },
    'time-120': { name: '2 minutes', rating: 2900, description: 'Grandmaster level' },
    'time-180': { name: '3 minutes', rating: 3100, description: 'World-class' },
    'time-240': { name: '4 minutes', rating: 3300, description: 'Engine level' },
    'time-300': { name: '5 minutes', rating: 3500, description: 'Maximum strength' }
  };

  const hybridAiLevels = {
    'custom': { name: '🎯 Custom Model', rating: '???', description: 'Choose a specific trained model' },
    'latest': { name: '🔥 Latest Model', rating: '???', description: 'Auto-loads your most recent training' }
  };

  const getCurrentAiLevels = () => {
    // Both C++ systems use the same depth levels
    return cppAiLevels;
  };

  // Fetch available models when component mounts
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const { API_BASE_URL } = await import('../utils/apiBase');
        const response = await fetch(`${API_BASE_URL}/models`);
        if (response.ok) {
          const data = await response.json();
          setAvailableModels(data.models);
          // Set default selected model to your 8000-game model if it exists
          const your8000Model = data.models.find(m => m.name === 'hybrid_v1_fast_2025-09-13T19-29-37');
          if (your8000Model) {
            setSelectedModel(your8000Model.name);
          } else if (data.models.length > 0) {
            setSelectedModel(data.models[0].name);
          }
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    };
    
    fetchModels();
  }, []);

  const timeLimitOptions = {
    '1': '1 minute',
    '2': '2 minutes',
    '3': '3 minutes',
    '5': '5 minutes',
    '10': '10 minutes',
    '15': '15 minutes',
    '30': '30 minutes',
    'unlimited': 'Unlimited'
  };

  // Convert algebraic notation to board coordinates
  const algebraicToCoords = (algebraic) => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i'];
    const ranks = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const file = algebraic[0];
    const rank = algebraic[1];
    const col = files.indexOf(file);
    const row = ranks.indexOf(rank);
    return { row, col };
  };

  // Create initial board
  const createInitialBoard = () => {
    const board = Array(9).fill(null).map(() => Array(9).fill(null));
    
    // Set up white pieces (bottom)
    board[0][0] = { type: 'donkey', color: 'white', hasMoved: false };
    board[0][1] = { type: 'knight', color: 'white', hasMoved: false };
    board[0][2] = { type: 'camel', color: 'white', hasMoved: false };
    board[0][3] = { type: 'advisor', color: 'white', hasMoved: false };
    board[0][4] = { type: 'king', color: 'white', hasMoved: false };
    board[0][5] = { type: 'advisor', color: 'white', hasMoved: false };
    board[0][6] = { type: 'camel', color: 'white', hasMoved: false };
    board[0][7] = { type: 'knight', color: 'white', hasMoved: false };
    board[0][8] = { type: 'donkey', color: 'white', hasMoved: false };

    board[1][1] = { type: 'cannon', color: 'white', hasMoved: false };
    board[1][3] = { type: 'ram', color: 'white', hasMoved: false };
    board[1][4] = { type: 'spy', color: 'white', hasMoved: false };
    board[1][5] = { type: 'ram', color: 'white', hasMoved: false };
    board[1][7] = { type: 'cannon', color: 'white', hasMoved: false };

    for (let col = 0; col < 9; col++) {
      board[2][col] = { type: 'pawn', color: 'white', hasMoved: false };
    }

    // Set up black pieces (top)
    for (let col = 0; col < 9; col++) {
      board[6][col] = { type: 'pawn', color: 'black', hasMoved: false };
    }

    board[7][1] = { type: 'cannon', color: 'black', hasMoved: false };
    board[7][3] = { type: 'ram', color: 'black', hasMoved: false };
    board[7][4] = { type: 'spy', color: 'black', hasMoved: false };
    board[7][5] = { type: 'ram', color: 'black', hasMoved: false };
    board[7][7] = { type: 'cannon', color: 'black', hasMoved: false };

    board[8][0] = { type: 'donkey', color: 'black', hasMoved: false };
    board[8][1] = { type: 'knight', color: 'black', hasMoved: false };
    board[8][2] = { type: 'camel', color: 'black', hasMoved: false };
    board[8][3] = { type: 'advisor', color: 'black', hasMoved: false };
    board[8][4] = { type: 'king', color: 'black', hasMoved: false };
    board[8][5] = { type: 'advisor', color: 'black', hasMoved: false };
    board[8][6] = { type: 'camel', color: 'black', hasMoved: false };
    board[8][7] = { type: 'knight', color: 'black', hasMoved: false };
    board[8][8] = { type: 'donkey', color: 'black', hasMoved: false };

    return board;
  };

  // Parse CSV file and extract game data
  const parseGameFile = (csvContent) => {
    const lines = csvContent.split('\n');
    const gameData = {
      whitePlayerName: '',
      blackPlayerName: '',
      gameMode: '',
      moves: []
    };

    let inGameInfo = false;
    let movesStarted = false;
    let headerSkipped = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) {
        // Empty line indicates transition to moves
        if (inGameInfo && !movesStarted) {
          movesStarted = true;
          inGameInfo = false;
        }
        continue;
      }

      const cells = line.split(',').map(cell => cell.replace(/"/g, '').trim());

      // Skip the initial header row
      if (i === 0 && cells[0] === 'Move Number') {
        headerSkipped = true;
        continue;
      }

      // Look for "Game Info" header
      if (headerSkipped && cells[0] === 'Game Info') {
        inGameInfo = true;
        continue;
      }

      // Parse game metadata (after "Game Info" header)
      if (inGameInfo && !movesStarted) {
        if (cells[0] === 'White Player') {
          gameData.whitePlayerName = cells[1];
        } else if (cells[0] === 'Black Player') {
          gameData.blackPlayerName = cells[1];
        } else if (cells[0] === 'Game Mode') {
          gameData.gameMode = cells[1];
        }
        continue;
      }

      // Parse moves (after empty row)
      if (movesStarted && cells[0] && !isNaN(parseInt(cells[0]))) {
        const move = {
          moveNumber: parseInt(cells[0]),
          player: cells[1],
          piece: cells[2],
          from: cells[3],
          to: cells[4],
          captured: cells[5],
          algebraic: cells[6]
        };
        gameData.moves.push(move);
      }
    }

    console.log('Parsed game data:', gameData); // Debug output
    return gameData;
  };

  // Parse board position from ASCII format
  const parseBoardPosition = (asciiBoard) => {
    const lines = asciiBoard.split('\n').filter(line => line.trim());
    const board = Array(9).fill(null).map(() => Array(9).fill(null));
    
    // Piece mapping from ASCII to piece types
    // Uppercase = white pieces, lowercase = black pieces
    const pieceMap = {
      // White pieces (uppercase)
      'K': { type: 'king', color: 'white' },
      'A': { type: 'advisor', color: 'white' },
      'M': { type: 'camel', color: 'white' },
      'N': { type: 'knight', color: 'white' },
      'D': { type: 'donkey', color: 'white' },
      'C': { type: 'cannon', color: 'white' },
      'R': { type: 'ram', color: 'white' },
      'S': { type: 'spy', color: 'white' },
      'P': { type: 'pawn', color: 'white' },
      // Black pieces (lowercase)
      'k': { type: 'king', color: 'black' },
      'a': { type: 'advisor', color: 'black' },
      'm': { type: 'camel', color: 'black' },
      'n': { type: 'knight', color: 'black' },
      'd': { type: 'donkey', color: 'black' },
      'c': { type: 'cannon', color: 'black' },
      'r': { type: 'ram', color: 'black' },
      's': { type: 'spy', color: 'black' },
      'p': { type: 'pawn', color: 'black' }
    };
    
    let boardStartRow = -1;
    
    // Find the board data (lines with rank numbers)
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Check for rank number followed by whitespace (space or tab)
      if (/^[1-9][\s\t]/.test(line)) {
        boardStartRow = i;
        break;
      }
    }
    
    if (boardStartRow === -1) {
      throw new Error('Invalid board format - no rank numbers found');
    }
    
    console.log('Board parsing started, found board at line:', boardStartRow);
    
    // Parse each rank (9 to 1, top to bottom)
    for (let rankIdx = 0; rankIdx < 9; rankIdx++) {
      const lineIdx = boardStartRow + rankIdx;
      if (lineIdx >= lines.length) break;
      
      const line = lines[lineIdx].trim();
      const parts = line.split('\t'); // Split on tabs to preserve column positions
      
      if (parts.length < 2) continue;
      
      const rank = parseInt(parts[0]);
      if (rank < 1 || rank > 9) continue;
      
      const boardRow = rank - 1; // Convert rank to array index (rank 1 = row 0)
      
      console.log(`Parsing rank ${rank} (row ${boardRow}):`, parts);
      
      // Parse pieces in this rank (skip the rank number at index 0)
      for (let fileIdx = 1; fileIdx < parts.length && fileIdx <= 9; fileIdx++) {
        const piece = parts[fileIdx].trim(); // Trim any extra whitespace
        const boardCol = fileIdx - 1;
        
        if (piece && piece !== '' && pieceMap[piece]) {
          const pieceInfo = pieceMap[piece];
          console.log(`Placing ${piece} (${pieceInfo.type}, ${pieceInfo.color}) at [${boardRow}][${boardCol}]`);
          
          board[boardRow][boardCol] = {
            type: pieceInfo.type,
            color: pieceInfo.color,
            hasMoved: false
          };
        } else {
          console.log(`  Col ${boardCol}: empty (piece="${piece}")`);
        }
      }
    }
    
    console.log('Final parsed board:', board);
    return board;
  };

  // Handle board position import
  const handleBoardPositionImport = (event) => {
    const text = event.target.value;
    if (!text.trim()) {
      setImportedGame(null);
      return;
    }
    
    try {
      const board = parseBoardPosition(text);
      
      setImportedGame({
        board,
        moveHistory: [],
        currentPlayer: 'white',
        whitePlayerName: '',
        blackPlayerName: '',
        gameMode: gameMode, // Use the actual game mode (ai, local, etc.)
        moveCount: 0
      });
      
      alert('Board position imported successfully!');
    } catch (error) {
      console.error('Error parsing board position:', error);
      alert('Error parsing board position. Please check the format.');
    }
  };

  // Handle file import
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const gameData = parseGameFile(csvContent);
        
        if (gameData.moves.length === 0) {
          alert('No moves found in the file. Please check the CSV format.');
          return;
        }
        
        // Set opponent name from imported data
        setOpponentName(gameData.blackPlayerName || 'Opponent');
        
        // Reconstruct game state from moves
        const board = createInitialBoard();
        const moveHistory = [];
        let currentPlayer = 'white';
        
        gameData.moves.forEach((move) => {
          const fromCoords = algebraicToCoords(move.from);
          const toCoords = algebraicToCoords(move.to);
          
          // Apply move to board
          const piece = board[fromCoords.row][fromCoords.col];
          if (piece) {
            board[toCoords.row][toCoords.col] = { ...piece, hasMoved: true };
            board[fromCoords.row][fromCoords.col] = null;
          }
          
          // Add to move history
          moveHistory.push({
            from: fromCoords,
            to: toCoords,
            piece: move.piece,
            captured: move.captured || null,
            player: move.player
          });
          
          currentPlayer = currentPlayer === 'white' ? 'black' : 'white';
        });
        
        setImportedGame({
          board,
          moveHistory,
          currentPlayer,
          whitePlayerName: gameData.whitePlayerName,
          blackPlayerName: gameData.blackPlayerName,
          gameMode: gameData.gameMode,
          moveCount: gameData.moves.length
        });
        
        alert(`Game imported successfully! ${gameData.moves.length} moves loaded.`);
      } catch (error) {
        console.error('Error parsing game file:', error);
        alert('Error parsing game file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  // Clear imported game
  const clearImportedGame = () => {
    setImportedGame(null);
    setOpponentName('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (gameMode === 'ai') {
      // No validation needed - user is already logged in
    } else {
      // For local games, only validate opponent name
      if (!opponentName.trim()) {
        newErrors.opponent = 'Opponent name is required';
      } else if (opponentName.trim().length < 2) {
        newErrors.opponent = 'Name must be at least 2 characters long';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const timeControls = {
        humanThinkingTime: humanTimeLimit === 'unlimited' ? null : parseInt(humanTimeLimit) * 60,
        aiThinkingTime: aiTimeLimit === 'unlimited' ? null : parseInt(aiTimeLimit) * 60,
        humanTotalTime: humanMoveTime === 'unlimited' ? null : parseInt(humanMoveTime) * 60,
        aiTotalTime: aiMoveTime === 'unlimited' ? null : parseInt(aiMoveTime) * 60
      };
      
      if (gameMode === 'ai') {
        const currentLevels = getCurrentAiLevels();
        const aiName = `${aiSystems[aiSystem].name} (${currentLevels[aiDifficulty].name})`;
        
        // Prepare AI configuration
        let aiConfig = { 
          system: aiSystem, 
          difficulty: aiDifficulty,
          useOpeningBook: useOpeningBook,
          useSmartTime: useSmartTime
        };
        if ((aiSystem === 'neural' || aiSystem === 'hybrid') && aiDifficulty === 'custom' && selectedModel) {
          aiConfig.customModel = selectedModel;
          console.log(`🎯 Selected custom model: ${selectedModel}`);
        }
        
        // For AI games, just pass the AI name as opponent
        onStartGame(aiName, timeControls, importedGame, aiConfig);
      } else {
        // For local games, pass the opponent's name
        onStartGame(opponentName.trim() || 'Opponent', timeControls, importedGame, null);
      }
    }
  };

  if (gameMode === 'ai') {
    return (
      <div className="player-setup">
        <div className="setup-container">
          <h2>Human vs Computer</h2>
          <p>Set up your game against the AI</p>
          
          <form onSubmit={handleSubmit} className="setup-form">
            {/* Import Game Section */}
            <div className="import-section">
              <h3>📁 Import Game (Optional)</h3>
              <div className="import-controls">
                <div className="import-tabs">
                  <button
                    type="button"
                    className={`tab-button ${importType === 'csv' ? 'active' : ''}`}
                    onClick={() => setImportType('csv')}
                  >
                    📄 CSV File
                  </button>
                  <button
                    type="button"
                    className={`tab-button ${importType === 'position' ? 'active' : ''}`}
                    onClick={() => setImportType('position')}
                  >
                    🎯 Board Position
                  </button>
                </div>
                
                {importType === 'csv' && (
                  <div className="import-method">
                    <p><strong>📄 CSV Game History Import:</strong></p>
                    <p>Import a previously exported game file with full move history. This will:</p>
                    <ul>
                      <li>Load all moves from the game</li>
                      <li>Set player names from the file</li>
                      <li>Reconstruct the current board position</li>
                      <li>Continue from where the imported game left off</li>
                    </ul>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileImport}
                      className="file-input"
                    />
                  </div>
                )}
                
                {importType === 'position' && (
                  <div className="import-method">
                    <p><strong>🎯 Board Position Import:</strong></p>
                    <p>Import from a specific board position (ASCII format). This will:</p>
                    <ul>
                      <li>Set up the board exactly as shown</li>
                      <li>Start a new game from this position</li>
                      <li>No move history will be loaded</li>
                      <li>Use uppercase for white pieces, lowercase for black pieces</li>
                    </ul>
                    <textarea
                      placeholder="Paste board position here (uppercase=white, lowercase=black):&#10;&#10;9	d	n	m	a	k	a	m	n	d&#10;8		c		r	s	r		c	&#10;7	p	p		p	p	p	p	p	p&#10;6			p						&#10;5									&#10;4					P	M			&#10;3	P	P	P	P		P	P	P	P&#10;2		C		R	S	R		C	&#10;1	D	N	M	A	K	A		N	D&#10;	a	b	c	d	e	f	g	h	i"
                      onChange={handleBoardPositionImport}
                      className="position-input"
                      rows="12"
                    />
                  </div>
                )}
                
                {importedGame && (
                  <div className="import-info">
                    <p>✅ Game imported: {importedGame.moveCount} moves</p>
                    <p>Players: {importedGame.whitePlayerName} vs {importedGame.blackPlayerName}</p>
                    <button type="button" onClick={clearImportedGame} className="clear-import">
                      Clear Import
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="player-input-group">
              <label>
                <div className="player-label">
                  <span className="player-icon">🎨</span>
                  Your Color
                </div>
              </label>
              <div className="color-selection">
                <label className={`color-option ${humanPlayerColor === 'white' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="color"
                    value="white"
                    checked={humanPlayerColor === 'white'}
                    onChange={(e) => setHumanPlayerColor(e.target.value)}
                  />
                  <span className="color-icon white-icon">♔</span>
                  White (First)
                </label>
                <label className={`color-option ${humanPlayerColor === 'black' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="color"
                    value="black"
                    checked={humanPlayerColor === 'black'}
                    onChange={(e) => setHumanPlayerColor(e.target.value)}
                  />
                  <span className="color-icon black-icon">♚</span>
                  Black (Second)
                </label>
              </div>
            </div>

            <div className="player-input-group">
              <label htmlFor="ai-system">
                <div className="player-label">
                  <span className="player-icon">🤖</span>
                  AI System
                </div>
              </label>
              <select
                id="ai-system"
                value={aiSystem}
                onChange={(e) => {
                  const newSystem = e.target.value;
                  setAiSystem(newSystem);
                  // Both systems use same depth levels, so keep current difficulty
                  // But reset to default if current difficulty doesn't exist
                  if (!cppAiLevels[aiDifficulty]) {
                    setAiDifficulty('depth-5'); // Default to depth 5
                  }
                }}
                className="system-select"
              >
                {Object.entries(aiSystems).map(([key, system]) => (
                  <option key={key} value={key}>
                    {system.icon} {system.name} - {system.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="player-input-group">
              <label htmlFor="ai-difficulty">
                <div className="player-label">
                  <span className="player-icon">{aiSystems[aiSystem].icon}</span>
                  AI Difficulty
                </div>
              </label>
              <select
                id="ai-difficulty"
                value={aiDifficulty}
                onChange={(e) => setAiDifficulty(e.target.value)}
                className="difficulty-select"
              >
                {Object.entries(getCurrentAiLevels()).map(([key, level]) => (
                  <option key={key} value={key}>
                    {level.name} ({level.rating}) - {level.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Advanced AI Options */}
            <div className="player-input-group advanced-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={useOpeningBook}
                  onChange={(e) => setUseOpeningBook(e.target.checked)}
                />
                <span className="checkbox-text">
                  <span className="option-icon">📚</span>
                  <span className="option-name">Use Opening Book</span>
                  <span className="option-description">
                    AI will use master-level opening moves when available (first 5-10 moves)
                  </span>
                </span>
              </label>
            </div>

            <div className="player-input-group">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={useSmartTime}
                  onChange={(e) => setUseSmartTime(e.target.checked)}
                />
                <span className="checkbox-text">
                  <span className="option-icon">⚡</span>
                  <span className="option-name">Smart Time Management</span>
                  <span className="option-description">
                    AI adjusts thinking time based on position evaluation (saves time in winning positions)
                  </span>
                </span>
              </label>
            </div>

            {/* Custom Model Selector - removed for C++ systems */}
            {false && (
              <div className="player-input-group">
                <label htmlFor="model-selector">
                  <div className="player-label">
                    <span className="player-icon">🎯</span>
                    Select Trained Model
                  </div>
                </label>
                <select
                  id="model-selector"
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="model-select"
                >
                  {availableModels.map((model) => (
                    <option key={model.name} value={model.name}>
                      {model.type === 'hybrid' ? '🚀' : '🧠'} {model.displayName} 
                      ({model.games} games, {model.size}MB)
                    </option>
                  ))}
                </select>
                {selectedModel && (
                  <div className="model-info">
                    {(() => {
                      const model = availableModels.find(m => m.name === selectedModel);
                      return model ? (
                        <div className="model-badge">
                          <div className="model-icon">{model.type === 'hybrid' ? '🚀' : '🧠'}</div>
                          <div className="model-details">
                            <div className="model-name">{model.displayName}</div>
                            <div className="model-stats">
                              {model.games} games • {model.size}MB • {model.type}
                            </div>
                            <div className="model-status">
                              {model.hasPolicyModel && '✅ Policy'} 
                              {model.hasKerasModel && ' ✅ Keras'}
                            </div>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="time-controls">
              <h3>⏱️ Time Controls</h3>
              <div className="time-controls-grid">
                <div className="player-input-group">
                  <label htmlFor="human-time">
                    <div className="player-label">
                      <span className="player-icon">👤</span>
                      Human Thinking Time
                    </div>
                  </label>
                  <select
                    id="human-time"
                    value={humanTimeLimit}
                    onChange={(e) => setHumanTimeLimit(e.target.value)}
                    className="time-select"
                  >
                    {Object.entries(timeLimitOptions).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="player-input-group">
                  <label htmlFor="ai-time">
                    <div className="player-label">
                      <span className="player-icon">🤖</span>
                      AI Thinking Time
                    </div>
                  </label>
                  <select
                    id="ai-time"
                    value={aiTimeLimit}
                    onChange={(e) => setAiTimeLimit(e.target.value)}
                    className="time-select"
                  >
                    {Object.entries(timeLimitOptions).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="player-input-group">
                  <label htmlFor="human-move-time">
                    <div className="player-label">
                      <span className="player-icon">👤</span>
                      Human Total Time
                    </div>
                  </label>
                  <select
                    id="human-move-time"
                    value={humanMoveTime}
                    onChange={(e) => setHumanMoveTime(e.target.value)}
                    className="time-select"
                  >
                    {Object.entries(timeLimitOptions).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="player-input-group">
                  <label htmlFor="ai-move-time">
                    <div className="player-label">
                      <span className="player-icon">🤖</span>
                      AI Total Time
                    </div>
                  </label>
                  <select
                    id="ai-move-time"
                    value={aiMoveTime}
                    onChange={(e) => setAiMoveTime(e.target.value)}
                    className="time-select"
                  >
                    {Object.entries(timeLimitOptions).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="ai-info">
              <div className="ai-badge">
                <span className="ai-icon">🤖</span>
                <div className="ai-details">
                  <div className="ai-name">AI Opponent</div>
                                  <div className="ai-level">{getCurrentAiLevels()[aiDifficulty].name}</div>
                <div className="ai-rating">Rating: {getCurrentAiLevels()[aiDifficulty].rating}</div>
                </div>
              </div>
            </div>

            <div className="setup-actions">
              <button type="button" onClick={onBack} className="back-button">
                ← Back
              </button>
              <button type="submit" className="start-button">
                Start Game
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="player-setup">
      <div className="setup-container">
        <h2>Set Up Players</h2>
        <p>Enter the names for both players to begin the game.</p>
        
        <form onSubmit={handleSubmit} className="setup-form">
          {/* Import Game Section */}
          <div className="import-section">
            <h3>📁 Import Game (Optional)</h3>
            <div className="import-controls">
              <div className="import-tabs">
                <button
                  type="button"
                  className={`tab-button ${importType === 'csv' ? 'active' : ''}`}
                  onClick={() => setImportType('csv')}
                >
                  📄 CSV File
                </button>
                <button
                  type="button"
                  className={`tab-button ${importType === 'position' ? 'active' : ''}`}
                  onClick={() => setImportType('position')}
                >
                  🎯 Board Position
                </button>
              </div>
              
                              {importType === 'csv' && (
                  <div className="import-method">
                    <p><strong>📄 CSV Game History Import:</strong></p>
                    <p>Import a previously exported game file with full move history. This will:</p>
                    <ul>
                      <li>Load all moves from the game</li>
                      <li>Set player names from the file</li>
                      <li>Reconstruct the current board position</li>
                      <li>Continue from where the imported game left off</li>
                    </ul>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileImport}
                      className="file-input"
                    />
                  </div>
                )}
                
                {importType === 'position' && (
                  <div className="import-method">
                    <p><strong>🎯 Board Position Import:</strong></p>
                    <p>Import from a specific board position (ASCII format). This will:</p>
                    <ul>
                      <li>Set up the board exactly as shown</li>
                      <li>Start a new game from this position</li>
                      <li>No move history will be loaded</li>
                      <li>Use uppercase for white pieces, lowercase for black pieces</li>
                    </ul>
                    <textarea
                      placeholder="Paste board position here (uppercase=white, lowercase=black):&#10;&#10;9	d	n	m	a	k	a	m	n	d&#10;8		c		r	s	r		c	&#10;7	p	p		p	p	p	p	p	p&#10;6			p						&#10;5									&#10;4					P	M			&#10;3	P	P	P	P		P	P	P	P&#10;2		C		R	S	R		C	&#10;1	D	N	M	A	K	A		N	D&#10;	a	b	c	d	e	f	g	h	i"
                     onChange={handleBoardPositionImport}
                     className="position-input"
                     rows="12"
                   />
                  </div>
                )}
              
              {importedGame && (
                <div className="import-info">
                  <p>✅ Game imported: {importedGame.moveCount} moves</p>
                  <p>Players: {importedGame.whitePlayerName} vs {importedGame.blackPlayerName}</p>
                  <button type="button" onClick={clearImportedGame} className="clear-import">
                    Clear Import
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="player-input-group">
            <label htmlFor="opponent-player-local">
              <div className="player-label">
                <span className="player-icon black-icon">♚</span>
                Opponent's Name
              </div>
            </label>
            <input
              id="opponent-player-local"
              type="text"
              value={opponentName}
              onChange={(e) => setOpponentName(e.target.value)}
              placeholder="Enter opponent's name"
              className={errors.opponent ? 'error' : ''}
              maxLength={50}
            />
            {errors.opponent && <span className="error-message">{errors.opponent}</span>}
            <p className="helper-text">You will play as White (first player)</p>
          </div>

          {errors.duplicate && (
            <div className="error-message global-error">{errors.duplicate}</div>
          )}

          <div className="time-controls">
            <h3>⏱️ Time Controls</h3>
            <div className="time-controls-grid">
              <div className="player-input-group">
                <label htmlFor="white-thinking-time">
                  <div className="player-label">
                    <span className="player-icon white-icon">♔</span>
                    White Thinking Time
                  </div>
                </label>
                <select
                  id="white-thinking-time"
                  value={humanTimeLimit}
                  onChange={(e) => setHumanTimeLimit(e.target.value)}
                  className="time-select"
                >
                  {Object.entries(timeLimitOptions).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="player-input-group">
                <label htmlFor="black-thinking-time">
                  <div className="player-label">
                    <span className="player-icon black-icon">♚</span>
                    Black Thinking Time
                  </div>
                </label>
                <select
                  id="black-thinking-time"
                  value={humanTimeLimit}
                  onChange={(e) => setHumanTimeLimit(e.target.value)}
                  className="time-select"
                >
                  {Object.entries(timeLimitOptions).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="player-input-group">
                <label htmlFor="white-move-time">
                  <div className="player-label">
                    <span className="player-icon white-icon">♔</span>
                    White Total Time
                  </div>
                </label>
                <select
                  id="white-move-time"
                  value={humanMoveTime}
                  onChange={(e) => setHumanMoveTime(e.target.value)}
                  className="time-select"
                >
                  {Object.entries(timeLimitOptions).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="player-input-group">
                <label htmlFor="black-move-time">
                  <div className="player-label">
                    <span className="player-icon black-icon">♚</span>
                    Black Total Time
                  </div>
                </label>
                <select
                  id="black-move-time"
                  value={humanMoveTime}
                  onChange={(e) => setHumanMoveTime(e.target.value)}
                  className="time-select"
                >
                  {Object.entries(timeLimitOptions).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="setup-actions">
            <button type="button" onClick={onBack} className="back-button">
              ← Back
            </button>
            <button type="submit" className="start-button">
              Start Game
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlayerSetup; 